import {
  Box,
  FormControlLabel,
  LinearProgress,
  Switch,
  Typography,
} from '@mui/material'
import { Alert } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useState } from 'react'

import { ONLY_DATE_TIME_FORMAT } from '../../../../../constants/global'
import { ITikTokAccount } from '../../../../../types/tiktok'
import { numberFormat } from '../../../../../utils'
import { useCostBackfillStatus } from '../hooks/useCostBackfillStatus'
import { useSaleOrders } from '../hooks/useSaleOrders'
import { useUnlinkedSkus } from '../hooks/useUnlinkedSkus'
import { IReportFilter } from '../types'
import CostBackfillButton from './CostBackfillButton'
import SaleOrdersTable from './SaleOrdersTable'
import UnlinkedSkusTable from './UnlinkedSkusTable'

interface Props {
  // ช่วงวันที่ + บัญชี มาจากตัวกรองด้านบนของหน้า (กดค้นหาแล้ว)
  filter: IReportFilter
  tiktokAccounts: ITikTokAccount[]
}

// แท็บรายการคำสั่งซื้อ: list ตามช่วงวันที่ที่เลือก + กรองเฉพาะที่ยังไม่มีต้นทุน + ปุ่มเติมต้นทุนย้อนหลัง
const SaleOrdersTab = ({ filter, tiktokAccounts }: Props) => {
  const [costMissing, setCostMissing] = useState<boolean>(false)
  // บังคับโหลดใหม่หลัง backfill เสร็จ (เปลี่ยนค่า → useSaleOrders reset ไปหน้าแรก)
  const [reloadKey, setReloadKey] = useState<number>(0)

  const reloadTable = useCallback(() => {
    setReloadKey((prev) => prev + 1)
  }, [])

  // สถานะจาก BE — ทนต่อ refresh หน้า (socket แจ้งได้เฉพาะตอนเปิดแท็บค้างไว้)
  const { status, isRunning, pending, refetch } =
    useCostBackfillStatus(reloadTable)

  const handleCompleted = useCallback(() => {
    reloadTable()
    refetch()
  }, [reloadTable, refetch])

  // ออเดอร์ที่ "ได้ต้นทุนจริง" ในรอบล่าสุด — updated คือจำนวน doc ที่เขียนกลับ
  // ซึ่งรวมใบที่เขียนแล้วต้นทุนยังไม่ครบด้วย จึงหัก stillMissing ออกก่อนโชว์
  const resolvedLastRun = Math.max(
    (status?.updated ?? 0) - (status?.stillMissing ?? 0),
    0,
  )

  const {
    items,
    isLoading,
    error,
    startIndex,
    hasNext,
    hasPrev,
    goNext,
    goPrev,
  } = useSaleOrders({
    enabled: true,
    startDate: filter.startDate,
    endDate: filter.endDate,
    shopId: filter.shopId,
    by: 'order',
    costMissing,
    reloadKey,
  })

  const unlinkedSkus = useUnlinkedSkus({
    enabled: costMissing,
    startDate: filter.startDate,
    endDate: filter.endDate,
    shopId: filter.shopId,
    reloadKey,
  })

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={costMissing}
                onChange={(e) => setCostMissing(e.target.checked)}
              />
            }
            label="เฉพาะที่ยังไม่มีต้นทุน"
          />
          <Typography variant="caption" color="text.secondary">
            คำสั่งซื้อที่หาต้นทุนสินค้าไม่เจอ เพราะ SKU ยังไม่ผูกกับสินค้าในระบบ
            หรือบันทึกไว้ก่อนระบบเก็บต้นทุน
          </Typography>
        </Box>
        <CostBackfillButton
          disabled={isRunning}
          onStarted={refetch}
          onCompleted={handleCompleted}
        />
      </Box>

      {isRunning && (
        <Box sx={{ mb: 2 }}>
          <Alert
            type="info"
            showIcon
            message={`กำลังเติมต้นทุนย้อนหลัง ${numberFormat(
              status?.scanned ?? 0,
              0,
            )} คำสั่งซื้อ`}
            description={
              status?.startedAt
                ? `เริ่มเมื่อ ${dayjs(status.startedAt).format(
                    ONLY_DATE_TIME_FORMAT,
                  )} — ปิดหน้านี้ได้ ระบบทำต่อจนเสร็จ`
                : 'ปิดหน้านี้ได้ ระบบทำต่อจนเสร็จ'
            }
          />
          <LinearProgress />
        </Box>
      )}

      {!isRunning && status?.status === 'FAILED' && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="เติมต้นทุนย้อนหลังรอบล่าสุดล้มเหลว"
          description={status.error ?? 'ไม่ทราบสาเหตุ — กดปุ่มเพื่อลองใหม่'}
        />
      )}

      {!isRunning && pending > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`ยังไม่มีต้นทุน ${numberFormat(pending, 0)} คำสั่งซื้อ`}
          description={
            status?.completedAt
              ? `รอบล่าสุด (${dayjs(status.completedAt).format(
                  ONLY_DATE_TIME_FORMAT,
                )}) ตรวจ ${numberFormat(status.scanned, 0)} คำสั่งซื้อ ` +
                `ได้ต้นทุน ${numberFormat(resolvedLastRun, 0)} คำสั่งซื้อ — ` +
                'ที่เหลือต้องผูก SKU ของ TikTok กับสินค้าในระบบก่อน แล้วกดเติมอีกครั้ง'
              : 'กดปุ่ม "เติมต้นทุนย้อนหลัง" เพื่อดึงต้นทุนจากสินค้าในระบบ'
          }
        />
      )}

      {!isRunning && status?.status === 'COMPLETED' && pending === 0 && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          message="ต้นทุนครบทุกคำสั่งซื้อแล้ว"
        />
      )}

      {costMissing && (
        <UnlinkedSkusTable
          items={unlinkedSkus.items}
          isLoading={unlinkedSkus.isLoading}
          error={unlinkedSkus.error}
          tiktokAccounts={tiktokAccounts}
        />
      )}

      <SaleOrdersTable
        items={items}
        isLoading={isLoading}
        error={error}
        startIndex={startIndex}
        hasNext={hasNext}
        hasPrev={hasPrev}
        goNext={goNext}
        goPrev={goPrev}
        scrollY={560}
      />
    </>
  )
}

export default SaleOrdersTab
